import type { StudyRunCaseSelectDto, StudyScoringOutputSelectDto, StudySelectDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import type { FC } from 'react';
import { formatRunCaseDate } from './meia-p4-run-cases.util';
import { formatBriefEnumLabel } from './meia-p5-brief.util';

type BriefAssumptionsTileProps = {
  scoringOutput?: StudyScoringOutputSelectDto | null;
  selectedRunCase: StudyRunCaseSelectDto | null;
  study: StudySelectDto;
};

export const BriefAssumptionsTile: FC<BriefAssumptionsTileProps> = (props) => {
  const { scoringOutput, selectedRunCase, study } = props;
  const assumptions = [
    {
      content: 'Operating environment matches the threat envelope captured in P1 Mission.',
      id: 'operating-environment',
    },
    {
      content: 'Authorities and engagement permissions remain valid through the selected epoch.',
      id: 'authorities',
    },
    {
      content: (
        <>
          Scoring aggregation uses{' '}
          <Badge
            className="align-middle"
            size="xs"
            variant="outline"
          >
            {formatBriefEnumLabel(study.scoringConfig.aggregationMethod)}
          </Badge>
          .
        </>
      ),
      id: 'aggregation',
    },
    {
      content: selectedRunCase ? (
        <>
          Run case epoch is{' '}
          <Badge
            className="align-middle"
            size="xs"
            variant="outline"
          >
            {formatBriefEnumLabel(selectedRunCase.epoch)}
          </Badge>
          .
        </>
      ) : (
        'No run case is selected.'
      ),
      id: 'epoch',
    },
    {
      content: scoringOutput
        ? `Recommendation reflects scoring output from ${formatRunCaseDate(scoringOutput.scoredAt)}.`
        : 'Recommendation will update after a run case is scored.',
      id: 'scoring-output',
    },
  ];

  return (
    <Tile header={{ title: 'Assumptions & Constraints' }}>
      <ul className="flex flex-col gap-2 text-xs text-gray-700">
        {assumptions.map((assumption) => (
          <li
            className="flex items-start gap-2"
            key={assumption.id}
          >
            <span className="mt-1 size-1.5 shrink-0 rounded-full bg-gray-300" />
            <span>{assumption.content}</span>
          </li>
        ))}
      </ul>
    </Tile>
  );
};
